import React from 'react';
import { Avatar, Button, Drawer, Empty, Skeleton, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faUser } from '@fortawesome/pro-solid-svg-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getIssues, IssueWithRelations } from '../../services/issues';
import { getPlannerPages, getTasks, Task } from '../../services/tasks';
import { useAuthStore } from '../../store/authStore';
import { colors } from '../../theme/colors';
import IssueDetail from '../Queue/IssueDetail';
import { getIssueDisplayTitle } from '../Queue/issueDisplay';
import TaskDetailModal from '../Planner/components/TaskDetailModal';
import './Dashboard.css';

const ISSUE_PRIORITY_ORDER: Record<string, number> = {
  LOWEST: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 4,
  HIGHEST: 5,
};

const TASK_PRIORITY_ORDER: Record<string, number> = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  URGENT: 4,
};

type DashboardTask = Task & { plannerPageId?: string };

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const selectedTaskId = searchParams.get('task');
  const selectedIssueId = searchParams.get('issue');

  const {
    data: plannerPages = [],
    isLoading: loadingPages,
    isError: pagesError,
    refetch: refetchPages,
  } = useQuery({
    queryKey: ['planner-pages'],
    queryFn: getPlannerPages,
  });

  const {
    data: issues = [],
    isLoading: loadingIssues,
    isError: issuesError,
    refetch: refetchIssues,
  } = useQuery({
    queryKey: ['issues'],
    queryFn: () => getIssues(),
  });

  const {
    data: tasks = [],
    isLoading: loadingTasks,
    isError: tasksError,
    refetch: refetchTasks,
  } = useQuery({
    queryKey: ['tasks', 'dashboard'],
    queryFn: () => getTasks(),
  });

  const isLoading = loadingPages || loadingIssues || loadingTasks;
  const isError = pagesError || issuesError || tasksError;

  const openTasks = React.useMemo(() => tasks.filter((task) => !task.completedAt), [tasks]);
  const myOpenTasks = React.useMemo(() => openTasks.filter((task) =>
    task.assigneeId === user?.id || task.assignee?.id === user?.id
  ), [openTasks, user?.id]);

  const overdueTasks = React.useMemo(() => myOpenTasks.filter((task) =>
    task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day')
  ), [myOpenTasks]);

  const dueThisWeek = React.useMemo(() => myOpenTasks.filter((task) => {
    if (!task.dueDate) return false;
    const dueDate = dayjs(task.dueDate);
    return !dueDate.isBefore(dayjs(), 'day') && dueDate.isBefore(dayjs().add(7, 'day').endOf('day'));
  }), [myOpenTasks]);

  const openIssues = React.useMemo(() => issues.filter((issue) => issue.status !== 'DONE'), [issues]);
  const highPriorityIssues = React.useMemo(() => openIssues.filter((issue) =>
    issue.priority === 'HIGH' || issue.priority === 'HIGHEST'
  ), [openIssues]);

  const sortedMyTasks = React.useMemo(() => [...myOpenTasks].sort((a, b) => {
    if (a.dueDate && b.dueDate) return dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf();
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return TASK_PRIORITY_ORDER[b.priority] - TASK_PRIORITY_ORDER[a.priority];
  }), [myOpenTasks]);

  const sortedIssues = React.useMemo(() => [...openIssues].sort((a, b) => {
    const priorityDifference = ISSUE_PRIORITY_ORDER[b.priority] - ISSUE_PRIORITY_ORDER[a.priority];
    if (priorityDifference !== 0) return priorityDifference;
    return dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf();
  }), [openIssues]);

  const plannerPageNames = React.useMemo(() => new Map(
    plannerPages.map((page) => [page.id, page.name])
  ), [plannerPages]);

  const selectedTask = tasks.find((task) => task.id === selectedTaskId) as DashboardTask | undefined;
  const selectedTaskPlannerPage = selectedTask?.plannerPageId
    ? plannerPages.find((page) => page.id === selectedTask.plannerPageId)
    : undefined;

  const openTask = (task: DashboardTask) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('task', task.id);
      next.delete('issue');
      return next;
    }, { replace: true });
  };

  const openIssue = (issue: IssueWithRelations) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('issue', issue.id);
      next.delete('task');
      return next;
    }, { replace: true });
  };

  const closeInspector = (key: 'task' | 'issue') => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete(key);
      return next;
    }, { replace: true });
  };

  const renderTaskRow = (task: DashboardTask, showOverdue = false) => {
    const overdue = Boolean(task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day'));
    const pageName = task.plannerPageId ? plannerPageNames.get(task.plannerPageId) : undefined;
    return (
      <button type="button" className="dashboard-work-row" key={task.id} onClick={() => openTask(task)}>
        <div className="dashboard-work-main">
          <span className="dashboard-work-title">{task.title}</span>
          <span className="dashboard-work-context">{pageName || task.status}</span>
        </div>
        <Tag bordered={false} className="dashboard-work-chip">{task.priority}</Tag>
        <span className={`dashboard-work-date${overdue && showOverdue ? ' dashboard-work-date--overdue' : ''}`}>
          {task.dueDate ? dayjs(task.dueDate).format('MMM D') : 'No due date'}
        </span>
        <FontAwesomeIcon icon={faArrowRight} className="dashboard-work-arrow" />
      </button>
    );
  };

  const renderIssueRow = (issue: IssueWithRelations) => (
    <button type="button" className="dashboard-work-row" key={issue.id} onClick={() => openIssue(issue)}>
      <div className="dashboard-work-main">
        <span className="dashboard-work-title">{getIssueDisplayTitle(issue.title, issue.description)}</span>
        <span className="dashboard-work-context">
          {issue.project?.name || issue.type} · {issue.status.replace(/_/g, ' ').toLowerCase()}
        </span>
      </div>
      <span className="dashboard-work-chips">
        <Tag bordered={false} className="dashboard-work-chip">{issue.type}</Tag>
        <Tag bordered={false} className="dashboard-work-chip">{issue.priority}</Tag>
      </span>
      <span className="dashboard-work-assignee">
        {issue.assignee ? (
          <>
            <Avatar size={20} src={issue.assignee.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
            <span>{issue.assignee.name}</span>
          </>
        ) : 'Unassigned'}
      </span>
      <FontAwesomeIcon icon={faArrowRight} className="dashboard-work-arrow" />
    </button>
  );

  const attentionItems = React.useMemo(() => [
    ...overdueTasks.map((task) => ({ kind: 'task' as const, task })),
    ...highPriorityIssues.map((issue) => ({ kind: 'issue' as const, issue })),
  ].slice(0, 6), [highPriorityIssues, overdueTasks]);

  const retryAll = () => {
    refetchPages();
    refetchTasks();
    refetchIssues();
  };

  return (
    <main className="dashboard-page" style={{ background: colors.bgBase }}>
      <header className="dashboard-header">
        <div>
          <h1>Today</h1>
          <p>{dayjs().format('dddd, MMMM D')} · Work requiring attention across Planner and Ops Queue.</p>
        </div>
      </header>

      {isLoading ? (
        <div className="dashboard-loading"><Skeleton active paragraph={{ rows: 10 }} /></div>
      ) : isError ? (
        <div className="dashboard-state">
          <strong>Workspace overview could not be loaded</strong>
          <span>Check the local backend and try again.</span>
          <Button onClick={retryAll}>Try again</Button>
        </div>
      ) : (
        <>
          <section className="dashboard-summary" aria-label="Workspace summary">
            <div><span>My open tasks</span><strong>{myOpenTasks.length}</strong></div>
            <div><span>Overdue</span><strong>{overdueTasks.length}</strong></div>
            <div><span>Due this week</span><strong>{dueThisWeek.length}</strong></div>
            <div><span>Open issues</span><strong>{openIssues.length}</strong></div>
            <div><span>High priority</span><strong>{highPriorityIssues.length}</strong></div>
          </section>

          <section className="dashboard-section dashboard-attention">
            <div className="dashboard-section-heading">
              <div>
                <h2>Needs attention</h2>
                <p>Overdue Planner work and high-priority Ops Queue items.</p>
              </div>
              <span>{attentionItems.length}</span>
            </div>
            {attentionItems.length === 0 ? (
              <div className="dashboard-empty-inline">
                <strong>Nothing urgent right now</strong>
                <span>Overdue tasks and high-priority issues will appear here.</span>
              </div>
            ) : (
              <div className="dashboard-attention-list">
                {attentionItems.map((item) => item.kind === 'task'
                  ? renderTaskRow(item.task as DashboardTask, true)
                  : renderIssueRow(item.issue)
                )}
              </div>
            )}
          </section>

          <div className="dashboard-work-grid">
            <section className="dashboard-section">
              <div className="dashboard-section-heading">
                <div>
                  <h2>My Planner work</h2>
                  <p>Your assigned tasks, ordered by due date.</p>
                </div>
                <Button type="text" onClick={() => navigate('/planner')}>View all</Button>
              </div>
              {sortedMyTasks.length === 0 ? (
                <div className="dashboard-section-empty">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open tasks assigned to you." />
                </div>
              ) : (
                <div>{sortedMyTasks.slice(0, 5).map((task) => renderTaskRow(task as DashboardTask, true))}</div>
              )}
            </section>

            <section className="dashboard-section">
              <div className="dashboard-section-heading">
                <div>
                  <h2>Ops Queue</h2>
                  <p>Open issues, ordered by priority.</p>
                </div>
                <Button type="text" onClick={() => navigate('/queue')}>View all</Button>
              </div>
              {sortedIssues.length === 0 ? (
                <div className="dashboard-section-empty">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="The Ops Queue is clear." />
                </div>
              ) : (
                <div>{sortedIssues.slice(0, 5).map(renderIssueRow)}</div>
              )}
            </section>
          </div>

          {selectedTaskId && (
            <TaskDetailModal
              taskId={selectedTaskId}
              open
              onClose={() => closeInspector('task')}
              onUpdate={() => queryClient.invalidateQueries({ queryKey: ['tasks'] })}
              plannerPage={selectedTaskPlannerPage}
            />
          )}

          <Drawer
            title="Ticket details"
            open={Boolean(selectedIssueId)}
            onClose={() => closeInspector('issue')}
            width="min(760px, 100vw)"
            styles={{
              header: { background: colors.bgBase, borderBottomColor: colors.border },
              body: { background: colors.bgBase },
            }}
          >
            {selectedIssueId && <IssueDetail issueId={selectedIssueId} embedded />}
          </Drawer>
        </>
      )}
    </main>
  );
};

export default Dashboard;
